<template>
  <q-page padding>
    <PageHeader
      title="Listagem de Máquinas"
      :backRoute="{ name: 'home' }"
      backBehavior="route"
    />

    <section class="row q-col-gutter-sm items-end q-mt-sm">
      <div class="col-md-2 col-sm-4 col-xs-12">
        <lista-unidades-select v-model="unidade"></lista-unidades-select>
      </div>

      <div class="margem-botoes">
        <q-btn
          color="primary"
          icon="refresh"
          label="Filtrar"
          no-caps
          outline
          @click="refreshCurrentTab"
        />
      </div>

      <div class="margem-botoes">
        <q-btn
          color="primary"
          icon="clear"
          label="Limpar Filtros"
          no-caps
          outline
          @click="clearFilter"
        />
      </div>

      <div class="col-grow text-right" style="margin-bottom: 2px">
        <q-btn
          v-if="canCreate"
          color="positive"
          label="Criar Nova Máquina"
          icon="add"
          no-caps
          @click="openCreateDialog"
        />
      </div>
    </section>

    <q-tabs
      v-model="tab"
      dense
      class="text-grey q-mt-md bg-accent"
      align="justify"
      active-color="primary"
      indicator-color="primary"
      narrow-indicator
    >
      <q-tab
        name="ativas"
        :label="`MÁQUINAS ATIVAS  (${paginationAtivas.rowsNumber})`"
      />
      <q-tab
        v-if="canViewTrashed"
        name="excluidas"
        :label="`MÁQUINAS EXCLUÍDAS  (${paginationExcluidas.rowsNumber})`"
      />
    </q-tabs>

    <div class="q-pt-md">
      <q-table
        flat
        dense
        table-header-class="bg-accent text-width-bold"
        :rows="filteredRowsCurrent"
        :columns="columnsCurrent"
        class="tabela-tamanho-fixo"
        :loading="loadingCurrent"
        v-model:pagination="paginationCurrent"
        hide-pagination
        :rows-per-page-options="[0]"
        row-key="id"
      >
        <template #top-right>
          <q-input
            outlined
            rounded
            dense
            debounce="300"
            v-model="filter"
            placeholder="Pesquisar"
          >
            <template #append>
              <q-icon name="search" />
            </template>
          </q-input>
        </template>

        <template #body-cell-credencial="props">
          <q-td>
            {{ props.row?.usuario_maquina?.sap || "" }}
          </q-td>
        </template>

        <template #body-cell-credencial_id="props">
          <q-td>
            {{
              tab === "excluidas"
                ? props.row?.usuario_maquina?.id || props.row?.user_id || ""
                : props.row?.usuario_maquina?.id || ""
            }}
          </q-td>
        </template>

        <template #body-cell-excluido_em="props">
          <q-td>
            {{ formatDateBR(props.row.deleted_at || props.row.updated_at) }}
          </q-td>
        </template>

        <template #body-cell-acoes="props">
          <q-td class="text-center">
            <div class="machine-actions">
              <q-icon
                class="pointer"
                size="sm"
                color="blue"
                name="info"
                @click="openDetailsDialog(props.row)"
              >
                <q-tooltip>Informações adicionais</q-tooltip>
              </q-icon>

              <q-icon
                v-if="tab === 'ativas' && canUpdate"
                class="pointer"
                size="sm"
                color="primary"
                name="edit"
                @click="openEditDialog(props.row)"
              >
                <q-tooltip>Editar</q-tooltip>
              </q-icon>

              <q-icon
                v-if="tab === 'ativas' && canDelete"
                class="pointer"
                size="sm"
                color="red"
                name="delete"
                @click="confirmDelete(props.row)"
              >
                <q-tooltip>Excluir</q-tooltip>
              </q-icon>

              <q-icon
                v-if="tab === 'excluidas' && canRestore"
                class="pointer"
                size="sm"
                color="positive"
                name="settings_backup_restore"
                @click="confirmRestore(props.row)"
              >
                <q-tooltip>Restaurar</q-tooltip>
              </q-icon>
            </div>
          </q-td>
        </template>
      </q-table>

      <div class="row justify-start q-mt-md">
        <q-pagination
          :disable="loadingCurrent"
          v-model="paginationCurrent.page"
          color="brand-5"
          active-color="secondary"
          active-text-color="white"
          :max="pagesNumber"
          :max-pages="5"
          :boundary-numbers="false"
          :direction-links="true"
          size="sm"
        />
      </div>
    </div>

    <q-dialog v-model="machineDialog.open" persistent>
      <q-card class="machine-dialog-card">
        <q-card-section class="row items-center q-pb-sm">
          <div>
            <div class="text-h5 text-weight-medium">{{ dialogTitle }}</div>
            <div
              v-if="machineDialog.mode === 'edit'"
              class="text-caption text-grey-7"
            >
              ID: {{ machineForm.id }}
            </div>
          </div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-separator />

        <q-card-section class="q-pt-md q-pb-lg">
          <div class="machine-section-title q-mb-md">DADOS OBRIGATÓRIOS</div>

          <div class="row q-col-gutter-md">
            <div
              class="col-md-6 col-xs-12"
              @mousedown.capture.prevent="openMachineTypeSelect"
            >
              <div class="machine-label">Tipo de Máquina *</div>
              <q-select
                ref="machineTypeSelect"
                class="machine-field"
                dense
                outlined
                placeholder="Selecionar..."
                v-model="machineForm.type"
                :options="tipoOptions"
                :loading="loadingTipoOptions"
                option-value="value"
                option-label="label"
                :display-value="machineForm.type?.label || 'Selecionar...'"
              />
            </div>
            <div
              class="col-md-6 col-xs-12"
              @mousedown.capture.prevent="openMachineEquipmentTypeSelect"
            >
              <div class="machine-label">Tipo de Equipamento</div>
              <q-select
                ref="machineEquipmentTypeSelect"
                class="machine-field"
                dense
                outlined
                placeholder="Selecionar..."
                v-model="machineForm.tipo_equipamento"
                :options="tipoEquipamentoOptions"
                :loading="loadingTipoEquipamentoOptions"
                option-value="value"
                option-label="label"
                :display-value="
                  machineForm.tipo_equipamento?.label || 'Selecionar...'
                "
                clearable
              />
            </div>
            <div class="col-12">
              <div class="machine-label">Nome do Equipamento *</div>
              <q-input
                class="machine-field"
                dense
                outlined
                placeholder="Ex: Corredor 3 — Loja Centro"
                v-model="machineForm.location"
              />
            </div>
            <template v-if="showMachineDimensions">
              <div class="col-md-6 col-xs-12">
                <div class="machine-label">{{ traysLabel }} *</div>
                <q-input
                  class="machine-field"
                  dense
                  outlined
                  placeholder="Ex: 3"
                  type="number"
                  min="1"
                  v-model.number="machineForm.trays"
                />
              </div>
              <div class="col-md-6 col-xs-12">
                <div class="machine-label">{{ columnsLabel }} *</div>
                <q-input
                  class="machine-field"
                  dense
                  outlined
                  placeholder="Ex: 9"
                  type="number"
                  min="1"
                  :max="columnsMax"
                  v-model.number="machineForm.columns"
                />
              </div>
            </template>
          </div>

          <div class="machine-section-title q-mt-lg q-mb-md">
            DADOS OPCIONAIS
          </div>

          <div class="row q-col-gutter-md">
            <div
              class="col-md-6 col-xs-12"
              @mousedown.capture.prevent="openMachineProductTypeSelect"
            >
              <div class="machine-label">Tipo de Produto</div>
              <q-select
                ref="machineProductTypeSelect"
                class="machine-field"
                dense
                outlined
                placeholder="Selecionar..."
                v-model="machineForm.tipo_produto"
                :options="tipoProdutoOptions"
                :loading="loadingTipoProdutoOptions"
                option-value="value"
                option-label="label"
                :display-value="
                  machineForm.tipo_produto?.label || 'Selecionar...'
                "
                clearable
              />
            </div>
            <div class="col-md-6 col-xs-12">
              <div class="machine-label">Patrimônio Tablet</div>
              <q-input
                class="machine-field"
                dense
                outlined
                maxlength="100"
                placeholder="TAB001"
                v-model="machineForm.patrimonio_tablet"
              />
            </div>
            <div class="col-md-6 col-xs-12">
              <div class="machine-label">Tipo de Conexão</div>
              <q-select
                class="machine-field"
                dense
                outlined
                placeholder="Selecionar..."
                v-model="machineForm.tipo_conexao"
                :options="tipoConexaoOptions"
                option-value="value"
                option-label="label"
                :display-value="
                  machineForm.tipo_conexao?.label || 'Selecionar...'
                "
                clearable
              />
            </div>
            <template v-if="isChipConnection">
              <div class="col-md-6 col-xs-12">
                <div class="machine-label">Proprietário do Chip</div>
                <q-select
                  class="machine-field"
                  dense
                  outlined
                  placeholder="Selecionar..."
                  v-model="machineForm.proprietario_chip"
                  :options="proprietarioChipOptions"
                  option-value="value"
                  option-label="label"
                  :display-value="
                    machineForm.proprietario_chip?.label || 'Selecionar...'
                  "
                />
              </div>
              <div class="col-md-6 col-xs-12">
                <div class="machine-label">Linha Telefônica</div>
                <q-input
                  class="machine-field"
                  dense
                  outlined
                  mask="(##) # ####-####"
                  unmasked-value
                  inputmode="numeric"
                  placeholder="Ex:(11) 9 9999-9999"
                  v-model="machineForm.linha_telefonica"
                />
              </div>
              <div class="col-md-6 col-xs-12">
                <div class="machine-label">Operadora</div>
                <q-select
                  class="machine-field"
                  dense
                  outlined
                  placeholder="Selecionar..."
                  v-model="machineForm.operadora"
                  :options="operadoraOptions"
                  option-value="value"
                  option-label="label"
                  :display-value="
                    machineForm.operadora?.label || 'Selecionar...'
                  "
                />
              </div>
            </template>
          </div>
        </q-card-section>

        <q-separator />

        <q-card-actions align="right" class="q-py-md q-px-lg">
          <q-btn flat label="Cancelar" color="grey-7" v-close-popup />
          <q-btn
            color="positive"
            no-caps
            :label="
              machineDialog.mode === 'create'
                ? 'CRIAR MÁQUINA'
                : 'SALVAR ALTERAÇÕES'
            "
            :loading="saving"
            @click="submitMachine"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="detailsDialog.open">
      <q-card class="machine-dialog-card">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Detalhes da Máquina</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section>
          <div class="row q-col-gutter-md">
            <div class="col-md-6 col-xs-12 detail-line">
              <q-icon name="tag" size="16px" class="detail-icon" /><strong
                >ID:</strong
              >
              {{ details.id }}
            </div>
            <div class="col-md-6 col-xs-12 detail-line">
              <q-icon name="person" size="16px" class="detail-icon" /><strong
                >Credencial:</strong
              >
              {{ details.credencial }}
            </div>
            <div class="col-md-6 col-xs-12 detail-line">
              <q-icon name="place" size="16px" class="detail-icon" /><strong
                >Localização:</strong
              >
              {{ details.location }}
            </div>
            <div class="col-md-6 col-xs-12 detail-line">
              <q-icon
                name="view_module"
                size="16px"
                class="detail-icon"
              /><strong>Bandejas:</strong> {{ details.trays }}
            </div>
            <div class="col-md-6 col-xs-12 detail-line">
              <q-icon name="grid_view" size="16px" class="detail-icon" /><strong
                >Posições:</strong
              >
              {{ details.columns }}
            </div>
            <div class="col-md-6 col-xs-12 detail-line">
              <q-icon
                name="precision_manufacturing"
                size="16px"
                class="detail-icon"
              /><strong>Tipo Máquina:</strong> {{ details.type }}
            </div>
            <div class="col-md-6 col-xs-12 detail-line">
              <q-icon name="category" size="16px" class="detail-icon" /><strong
                >Tipo Produto:</strong
              >
              {{ details.tipo_produto }}
            </div>
            <div class="col-md-6 col-xs-12 detail-line">
              <q-icon
                name="inventory_2"
                size="16px"
                class="detail-icon"
              /><strong>Tipo Equipamento:</strong>
              {{ details.tipo_equipamento }}
            </div>
            <div class="col-md-6 col-xs-12 detail-line">
              <q-icon name="wifi" size="16px" class="detail-icon" /><strong
                >Tipo Conexão:</strong
              >
              {{ details.tipo_conexao }}
            </div>
            <div class="col-md-6 col-xs-12 detail-line">
              <q-icon name="sim_card" size="16px" class="detail-icon" /><strong
                >Proprietário Chip:</strong
              >
              {{ details.proprietario_chip }}
            </div>
            <div class="col-md-6 col-xs-12 detail-line">
              <q-icon name="call" size="16px" class="detail-icon" /><strong
                >Linha Telefônica:</strong
              >
              {{ formatPhoneNumber(details.linha_telefonica) }}
            </div>
            <div class="col-md-6 col-xs-12 detail-line">
              <q-icon
                name="settings_input_antenna"
                size="16px"
                class="detail-icon"
              /><strong>Operadora:</strong> {{ details.operadora }}
            </div>
            <div class="col-md-6 col-xs-12 detail-line">
              <q-icon name="sell" size="16px" class="detail-icon" /><strong
                >Patrimônio Tablet:</strong
              >
              {{ details.patrimonio_tablet }}
            </div>
            <div class="col-md-6 col-xs-12 detail-line">
              <q-icon
                name="event_busy"
                size="16px"
                class="detail-icon"
              /><strong>Excluído em:</strong> {{ details.deleted_at }}
            </div>
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<script>
import { ref, computed, nextTick, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useQuasar } from "quasar";
import ListaUnidadesSelect from "../../../organizacional/unidade/component/ListaUnidadesSelect.vue";
import PageHeader from "../../../../components/PageHeader.vue";

const columnsAtivas = [
  {
    name: "id",
    label: "ID",
    field: "id",
    align: "left",
  },
  {
    name: "credencial_id",
    label: "ID CREDENCIAL",
    field: (row) => row?.usuario_maquina?.id,
    align: "left",
  },
  {
    name: "credencial",
    label: "CREDENCIAL",
    field: (row) => row?.usuario_maquina?.sap,
    align: "left",
  },
  {
    name: "location",
    label: "LOCALIZAÇÃO",
    field: "location",
    align: "left",
  },
  /*
  {
    name: "trays",
    label: "BANDEJAS",
    field: "trays",
    align: "left",
  },
  {
    name: "columns",
    label: "POSIÇÕES",
    field: "columns",
    align: "left",
  },
  */
  {
    name: "tipo_conexao",
    label: "CONEXÃO",
    field: "tipo_conexao",
    align: "left",
  },
  {
    name: "tipo_equipamento",
    label: "TIPO EQUIPAMENTO",
    field: "tipo_equipamento",
    align: "left",
  },
  {
    name: "acoes",
    label: "AÇÕES",
    field: "acoes",
    align: "center",
  },
];

const columnsExcluidas = [
  {
    name: "id",
    label: "ID",
    field: "id",
    align: "left",
  },
  {
    name: "credencial_id",
    label: "ID CREDENCIAL",
    field: (row) => row?.usuario_maquina?.id || row?.user_id,
    align: "left",
  },
  {
    name: "credencial",
    label: "CREDENCIAL",
    field: (row) => row?.usuario_maquina?.sap,
    align: "left",
  },
  {
    name: "location",
    label: "LOCALIZAÇÃO",
    field: "location",
    align: "left",
  },
  /*
  {
    name: "trays",
    label: "BANDEJAS",
    field: "trays",
    align: "left",
  },
  {
    name: "columns",
    label: "POSIÇÕES",
    field: "columns",
    align: "left",
  },
  */
  {
    name: "tipo_conexao",
    label: "CONEXÃO",
    field: "tipo_conexao",
    align: "left",
  },
  {
    name: "tipo_equipamento",
    label: "TIPO EQUIPAMENTO",
    field: "tipo_equipamento",
    align: "left",
  },
  {
    name: "excluido_em",
    label: "EXCLUÍDO EM",
    field: "deleted_at",
    align: "left",
  },
  {
    name: "acoes",
    label: "AÇÕES",
    field: "acoes",
    align: "center",
  },
];

const tipoConexaoOptions = [
  { label: "Cabo", value: "cabo" },
  { label: "Wi-fi", value: "wifi" },
  { label: "Chip", value: "chip" },
];

const proprietarioChipOptions = [
  { label: "Multpex", value: "multpex" },
  { label: "Cliente", value: "cliente" },
];

const operadoraOptions = [
  { label: "Claro", value: "Claro" },
  { label: "Vivo", value: "Vivo" },
  { label: "Tim", value: "Tim" },
];

const defaultMachineForm = () => ({
  id: null,
  trays: "",
  columns: "",
  location: "",
  type: null,
  user_id: null,
  tipo_produto: null,
  tipo_equipamento: null,
  patrimonio_tablet: "",
  tipo_conexao: null,
  proprietario_chip: null,
  linha_telefonica: "",
  operadora: "",
});

const defaultDetail = () => ({
  id: "",
  credencial: "",
  location: "",
  trays: "",
  columns: "",
  type: "",
  tipo_produto: "",
  tipo_equipamento: "",
  tipo_conexao: "",
  proprietario_chip: "",
  linha_telefonica: "",
  operadora: "",
  patrimonio_tablet: "",
  deleted_at: "",
});

export default {
  components: { ListaUnidadesSelect, PageHeader },
  setup() {
    const store = useStore();
    const $q = useQuasar();

    const tab = ref("ativas");
    const unidade = ref();
    const filter = ref("");
    const rowsAtivas = ref([]);
    const rowsExcluidas = ref([]);
    const loadingAtivas = ref(false);
    const loadingExcluidas = ref(false);
    const saving = ref(false);
    const machineDialog = ref({ open: false, mode: "create" });
    const machineForm = ref(defaultMachineForm());
    const detailsDialog = ref({ open: false });
    const details = ref(defaultDetail());
    const allUserOptions = ref([]);
    const userOptions = ref([]);
    const tipoOptions = ref([]);
    const tipoEquipamentoOptions = ref([]);
    const tipoProdutoOptions = ref([]);
    const loadingTipoOptions = ref(false);
    const loadingTipoEquipamentoOptions = ref(false);
    const loadingTipoProdutoOptions = ref(false);
    const machineTypeSelect = ref(null);
    const machineEquipmentTypeSelect = ref(null);
    const machineProductTypeSelect = ref(null);

    const paginationAtivas = ref({
      sortBy: "desc",
      descending: false,
      page: 1,
      rowsPerPage: 50,
      rowsNumber: 0,
    });

    const paginationExcluidas = ref({
      sortBy: "desc",
      descending: false,
      page: 1,
      rowsPerPage: 50,
      rowsNumber: 0,
    });

    const hasPermission = (permission) => {
      const userPermissions =
        store.state.autenticacao?.usuarioLogado?.permissoes?.gmapi?.roles || [];
      return userPermissions.includes(permission);
    };

    const canCreate = computed(() => hasPermission("machine-create"));
    const canUpdate = computed(() => hasPermission("machine-update"));
    const canDelete = computed(() => hasPermission("machine-delete"));
    const canRestore = computed(() => hasPermission("machine-restore"));
    const canViewTrashed = computed(() => hasPermission("machine-trashed"));

    const vendorId = computed(
      () => store.state.org?.unidadeData?.id || unidade.value?.id || null
    );

    const rowsCurrent = computed(() =>
      tab.value === "ativas" ? rowsAtivas.value : rowsExcluidas.value
    );

    const normalizeSearchText = (value) =>
      String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();

    const buildMachineSearchText = (row) =>
      [
        row?.id,
        row?.user_id,
        row?.usuario_maquina?.id,
        row?.usuario_maquina?.sap,
        row?.location,
        row?.trays,
        row?.columns,
        row?.type,
        row?.tipo_produto,
        row?.tipo_equipamento,
        row?.tipo_conexao,
      ]
        .map((item) => String(item || ""))
        .join(" ");

    const filteredRowsCurrent = computed(() => {
      const rows = rowsCurrent.value;
      const term = normalizeSearchText(filter.value);

      if (!term) {
        return rows;
      }

      return rows.filter((row) =>
        normalizeSearchText(buildMachineSearchText(row)).includes(term)
      );
    });

    const columnsCurrent = computed(() =>
      tab.value === "ativas" ? columnsAtivas : columnsExcluidas
    );

    const loadingCurrent = computed(() =>
      tab.value === "ativas" ? loadingAtivas.value : loadingExcluidas.value
    );

    const paginationCurrent = computed({
      get: () =>
        tab.value === "ativas"
          ? paginationAtivas.value
          : paginationExcluidas.value,
      set: (value) => {
        if (tab.value === "ativas") {
          paginationAtivas.value = value;
          return;
        }
        paginationExcluidas.value = value;
      },
    });

    const pagesNumber = computed(() => {
      const source =
        tab.value === "ativas"
          ? paginationAtivas.value
          : paginationExcluidas.value;
      if (!source.rowsPerPage) {
        return 1;
      }
      return Math.max(1, Math.ceil(source.rowsNumber / source.rowsPerPage));
    });

    const dialogTitle = computed(() =>
      machineDialog.value.mode === "create"
        ? "Criar Nova Máquina"
        : "Editar Máquina"
    );

    const clearFilter = () => {
      filter.value = "";
    };

    const getUnidade = () => {
      if (
        localStorage.unidade &&
        localStorage.unidade !== null &&
        localStorage.unidade !== "null"
      ) {
        const getunidade = localStorage.getItem("unidade");
        unidade.value = JSON.parse(getunidade);
      }
    };

    const normalizeResults = (response) => {
      const results = response?.data?.results || {};
      return {
        rows: Array.isArray(results?.data) ? results.data : [],
        total: Number(results?.total || 0),
        perPage: Number(results?.per_page || 50),
        page: Number(results?.current_page || 1),
      };
    };

    const applyPagination = (
      targetPagination,
      normalized,
      fallbackPage,
      fallbackRowsPerPage
    ) => {
      targetPagination.value.rowsNumber = normalized.total;
      targetPagination.value.rowsPerPage = Number(
        normalized.perPage || fallbackRowsPerPage
      );
      targetPagination.value.page = Number(normalized.page || fallbackPage);
    };

    const notifyError = (message) => {
      $q.notify({
        message,
        color: "negative",
        actions: [{ label: "Fechar", color: "white" }],
      });
    };

    const notifySuccess = (message) => {
      $q.notify({
        message,
        color: "positive",
        actions: [{ label: "Fechar", color: "white" }],
      });
    };

    const getMachine = async (
      page = paginationAtivas.value.page,
      rowsPerPage = paginationAtivas.value.rowsPerPage
    ) => {
      if (!vendorId.value) {
        rowsAtivas.value = [];
        paginationAtivas.value.rowsNumber = 0;
        return;
      }

      loadingAtivas.value = true;

      try {
        const response = await store.dispatch("adminMultpex/getMachine", {
          vendor_id: vendorId.value,
          per_page: rowsPerPage,
          page,
        });

        const normalized = normalizeResults(response);
        rowsAtivas.value = normalized.rows;
        applyPagination(paginationAtivas, normalized, page, rowsPerPage);
      } catch (error) {
        rowsAtivas.value = [];
        paginationAtivas.value.rowsNumber = 0;
        notifyError("Erro ao carregar máquinas ativas");
      } finally {
        loadingAtivas.value = false;
      }
    };

    const getMachineTrashed = async (
      page = paginationExcluidas.value.page,
      rowsPerPage = paginationExcluidas.value.rowsPerPage
    ) => {
      if (!canViewTrashed.value || !vendorId.value) {
        rowsExcluidas.value = [];
        paginationExcluidas.value.rowsNumber = 0;
        return;
      }

      loadingExcluidas.value = true;

      try {
        const response = await store.dispatch(
          "adminMultpex/getMachineTrashed",
          {
            vendor_id: vendorId.value,
            per_page: rowsPerPage,
            page,
          }
        );

        const normalized = normalizeResults(response);
        rowsExcluidas.value = normalized.rows;
        applyPagination(paginationExcluidas, normalized, page, rowsPerPage);
      } catch (error) {
        rowsExcluidas.value = [];
        paginationExcluidas.value.rowsNumber = 0;
        notifyError("Erro ao carregar máquinas excluídas");
      } finally {
        loadingExcluidas.value = false;
      }
    };

    const refreshCurrentTab = async () => {
      if (tab.value === "ativas") {
        await getMachine();
        return;
      }
      await getMachineTrashed();
    };

    const normalizeNullable = (value) => {
      if (value === null || value === undefined) {
        return null;
      }

      const text = String(value).trim();
      return text ? text : null;
    };

    const normalizeNumber = (value) => {
      if (value === null || value === undefined || value === "") {
        return null;
      }

      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const normalizeApiResults = (response) =>
      Array.isArray(response?.data?.results) ? response.data.results : [];

    const normalizeMachineTypeValue = (value) => {
      const normalized = String(value || "").toLowerCase();
      return normalized === "qrcode" ? "qrcode" : normalized;
    };

    const normalizeProductTypeValue = (value) => {
      const normalized = String(value || "").toLowerCase();
      const productTypeAliases = {
        epis: "epi",
        epi: "epi",
        "não-epi": "nao-epi",
        "nao-epi": "nao-epi",
        "kit requisição": "KIT Requisição",
        "kit requisiçao": "KIT Requisição",
        "kit requisicao": "KIT Requisição",
        todos: "todos",
      };

      return productTypeAliases[normalized] || value;
    };

    const machineTypeLabels = {
      armario: "Armário",
      balcao: "Balcão",
      maquina: "Máquina",
      qrcode: "QrCode",
    };

    const productTypeLabels = {
      epi: "EPIs",
      "nao-epi": "Não-EPI",
      todos: "Todos",
    };

    const toSelectOption = (name, labelMap = null, normalizeValue = null) => {
      const value = normalizeValue ? normalizeValue(name) : name;
      return {
        label: labelMap?.[value] || name,
        value,
      };
    };

    const loadSelectOptions = async ({
      options,
      loading,
      action,
      labelMap,
      normalizeValue,
    }) => {
      if (loading.value) {
        return;
      }

      loading.value = true;
      try {
        const response = await store.dispatch(action);
        options.value = normalizeApiResults(response).map((item) =>
          toSelectOption(item?.name, labelMap, normalizeValue)
        );
      } catch (error) {
        options.value = [];
        notifyError("Erro ao carregar opções do campo");
      } finally {
        loading.value = false;
      }
    };

    const openSelectWithOptions = async (loadOptions, selectRef) => {
      await loadOptions();
      await nextTick();
      selectRef.value?.showPopup?.();
    };

    const loadMachineTypeOptions = () =>
      loadSelectOptions({
        options: tipoOptions,
        loading: loadingTipoOptions,
        action: "adminMultpex/getMachineTypes",
        labelMap: machineTypeLabels,
        normalizeValue: normalizeMachineTypeValue,
      });

    const loadMachineEquipmentTypeOptions = () =>
      loadSelectOptions({
        options: tipoEquipamentoOptions,
        loading: loadingTipoEquipamentoOptions,
        action: "adminMultpex/getMachineEquipmentTypes",
      });

    const loadMachineProductTypeOptions = () =>
      loadSelectOptions({
        options: tipoProdutoOptions,
        loading: loadingTipoProdutoOptions,
        action: "adminMultpex/getMachineProductTypes",
        labelMap: productTypeLabels,
        normalizeValue: normalizeProductTypeValue,
      });

    const openMachineTypeSelect = () =>
      openSelectWithOptions(loadMachineTypeOptions, machineTypeSelect);

    const openMachineEquipmentTypeSelect = () =>
      openSelectWithOptions(
        loadMachineEquipmentTypeOptions,
        machineEquipmentTypeSelect
      );

    const openMachineProductTypeSelect = () =>
      openSelectWithOptions(
        loadMachineProductTypeOptions,
        machineProductTypeSelect
      );

    const extractSelectValue = (val) => {
      if (!val) return null;
      return typeof val === "object" ? val.value : val;
    };

    const machineTypeRequiresDimensions = (type) =>
      ["armario", "maquina"].includes(type);

    const machineTypeShowsDimensions = (type) =>
      !["balcao", "qrcode"].includes(type);

    const isEditingArmarioMachine = computed(
      () =>
        machineDialog.value.mode === "edit" &&
        extractSelectValue(machineForm.value.type) === "armario"
    );

    const requiresMachineDimensions = computed(
      () =>
        !isEditingArmarioMachine.value &&
        machineTypeRequiresDimensions(
          extractSelectValue(machineForm.value.type)
        )
    );

    const showMachineDimensions = computed(
      () =>
        !isEditingArmarioMachine.value &&
        machineTypeShowsDimensions(extractSelectValue(machineForm.value.type))
    );

    const isArmarioMachine = computed(
      () => extractSelectValue(machineForm.value.type) === "armario"
    );

    const traysLabel = computed(() =>
      isArmarioMachine.value ? "Módulos" : "Bandejas"
    );

    const columnsLabel = computed(() =>
      isArmarioMachine.value ? "Portas" : "Posições"
    );

    const columnsMax = computed(() => (isArmarioMachine.value ? undefined : 9));

    const validateMachineForm = () => {
      if (!vendorId.value) {
        notifyError("Selecione uma unidade antes de criar uma máquina");
        return false;
      }

      if (!machineForm.value.location || !machineForm.value.location.trim()) {
        notifyError("O campo Localização é obrigatório");
        return false;
      }

      const typeValue = extractSelectValue(machineForm.value.type);
      if (!typeValue) {
        notifyError("O campo Tipo de Máquina é obrigatório");
        return false;
      }

      if (requiresMachineDimensions.value) {
        const trays = normalizeNumber(machineForm.value.trays);
        if (!trays || trays < 1) {
          notifyError(`O campo ${traysLabel.value} deve ser maior que zero`);
          return false;
        }

        const columns = normalizeNumber(machineForm.value.columns);
        if (!columns || columns < 1) {
          notifyError(`O campo ${columnsLabel.value} deve ser maior que zero`);
          return false;
        }

        if (!isArmarioMachine.value && columns > 9) {
          notifyError(`O campo ${columnsLabel.value} deve estar entre 1 e 9`);
          return false;
        }
      }

      return true;
    };

    const openCreateDialog = () => {
      machineDialog.value = { open: true, mode: "create" };
      machineForm.value = defaultMachineForm();
    };

    const isChipConnection = computed(
      () => extractSelectValue(machineForm.value.tipo_conexao) === "chip"
    );

    const findOptionByValue = (options, value, normalizeValue = null) => {
      if (!value) return null;
      const normalized = normalizeValue ? normalizeValue(value) : value;
      return options.find((opt) => opt.value === normalized) || null;
    };

    const openEditDialog = async (row) => {
      await Promise.all([
        loadMachineTypeOptions(),
        loadMachineEquipmentTypeOptions(),
        loadMachineProductTypeOptions(),
      ]);

      machineDialog.value = { open: true, mode: "edit" };

      const typeOption = findOptionByValue(
        tipoOptions.value,
        row?.type,
        normalizeMachineTypeValue
      );
      const tipoEquipamentoOption = findOptionByValue(
        tipoEquipamentoOptions.value,
        row?.tipo_equipamento
      );
      const tipoProdutoOption = findOptionByValue(
        tipoProdutoOptions.value,
        row?.tipo_produto,
        normalizeProductTypeValue
      );
      const tipoConexaoOption = findOptionByValue(
        tipoConexaoOptions,
        row?.tipo_conexao
      );
      const proprietarioChipOption = findOptionByValue(
        proprietarioChipOptions,
        row?.proprietario_chip
      );
      const operadoraOption = findOptionByValue(
        operadoraOptions,
        row?.operadora
      );

      machineForm.value = {
        id: row?.id || null,
        trays: row?.trays || "",
        columns: row?.columns || "",
        location: row?.location || "",
        type: typeOption,
        user_id: row?.user_id || row?.usuario_maquina?.id || null,
        tipo_produto: tipoProdutoOption,
        tipo_equipamento: tipoEquipamentoOption,
        patrimonio_tablet: row?.patrimonio_chip || row?.patrimonio_tablet || "",
        tipo_conexao: tipoConexaoOption,
        proprietario_chip: proprietarioChipOption,
        linha_telefonica: row?.linha_telefonica || "",
        operadora: operadoraOption,
      };
    };

    const formatDateBR = (date) => {
      if (!date) {
        return "";
      }

      const split = String(date).split(" ")[0].split("-");
      if (split.length !== 3) {
        return date;
      }
      return `${split[2]}/${split[1]}/${split[0]}`;
    };

    const formatPhoneNumber = (value) => {
      const digits = String(value || "").replace(/\D/g, "");

      if (digits.length === 11) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 3)} ${digits.slice(
          3,
          7
        )}-${digits.slice(7)}`;
      }

      if (digits.length === 10) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(
          6
        )}`;
      }

      return value || "";
    };

    const openDetailsDialog = (row) => {
      details.value = {
        id: row?.id || "",
        credencial: row?.usuario_maquina?.sap || "",
        location: row?.location || "",
        trays: row?.trays,
        columns: row?.comuns,
        type: row?.type || "",
        tipo_produto: row?.tipo_produto || "",
        tipo_equipamento: row?.tipo_equipamento || "",
        tipo_conexao: row?.tipo_conexao || "",
        proprietario_chip: row?.proprietario_chip || "",
        linha_telefonica: row?.linha_telefonica || "",
        operadora: row?.operadora || "",
        patrimonio_tablet: row?.patrimonio_tablet || row?.patrimonio_chip || "",
        deleted_at: formatDateBR(row?.deleted_at),
      };
      detailsDialog.value.open = true;
    };

    const getDimensionsPayload = () => {
      if (!requiresMachineDimensions.value) {
        return {
          trays: null,
          columns: null,
        };
      }

      return {
        trays: Number(machineForm.value.trays),
        columns: Number(machineForm.value.columns),
      };
    };

    const createPayload = () => {
      const typeValue = extractSelectValue(machineForm.value.type);
      const payload = {
        vendor_id: Number(vendorId.value),
        location: String(machineForm.value.location || "").trim(),
        type: typeValue,
        ...getDimensionsPayload(),
      };

      const userId = normalizeNumber(machineForm.value.user_id);
      if (userId) {
        payload.user_id = userId;
      }

      const tipoProdutoValue = extractSelectValue(
        machineForm.value.tipo_produto
      );
      const tipoProduto = normalizeNullable(tipoProdutoValue);
      if (tipoProduto) {
        payload.tipo_produto = tipoProduto;
      }

      const tipoEquipamentoValue = extractSelectValue(
        machineForm.value.tipo_equipamento
      );
      const tipoEquipamento = normalizeNullable(tipoEquipamentoValue);
      if (tipoEquipamento) {
        payload.tipo_equipamento = tipoEquipamento;
      }

      const patrimonio = normalizeNullable(machineForm.value.patrimonio_tablet);
      if (patrimonio) {
        payload.patrimonio_tablet = patrimonio;
      }

      const conexaoValue = extractSelectValue(machineForm.value.tipo_conexao);
      const conexao = normalizeNullable(conexaoValue);
      if (conexao) {
        payload.tipo_conexao = conexao;
      }

      if (conexao === "chip") {
        const proprietario = normalizeNullable(
          extractSelectValue(machineForm.value.proprietario_chip)
        );
        if (proprietario) {
          payload.proprietario_chip = proprietario;
        }

        const linha = normalizeNullable(machineForm.value.linha_telefonica);
        if (linha) {
          payload.linha_telefonica = linha;
        }

        const operadora = normalizeNullable(
          extractSelectValue(machineForm.value.operadora)
        );
        if (operadora) {
          payload.operadora = operadora;
        }
      }

      return payload;
    };

    const updatePayload = () => {
      const payload = {};

      if (machineForm.value.location && machineForm.value.location.trim()) {
        payload.location = machineForm.value.location.trim();
      }

      const typeValue = extractSelectValue(machineForm.value.type);
      if (typeValue) {
        payload.type = typeValue;
      }

      Object.assign(payload, getDimensionsPayload());

      const userId = normalizeNumber(machineForm.value.user_id);
      if (userId) {
        payload.user_id = userId;
      }

      const tipoProdutoValue = extractSelectValue(
        machineForm.value.tipo_produto
      );
      payload.tipo_produto = normalizeNullable(tipoProdutoValue);

      const tipoEquipamentoValue = extractSelectValue(
        machineForm.value.tipo_equipamento
      );
      payload.tipo_equipamento = normalizeNullable(tipoEquipamentoValue);

      payload.patrimonio_tablet = normalizeNullable(
        machineForm.value.patrimonio_tablet
      );

      const conexaoValue = extractSelectValue(machineForm.value.tipo_conexao);
      payload.tipo_conexao = normalizeNullable(conexaoValue);

      if (payload.tipo_conexao === "chip") {
        payload.proprietario_chip = normalizeNullable(
          extractSelectValue(machineForm.value.proprietario_chip)
        );
        payload.linha_telefonica = normalizeNullable(
          machineForm.value.linha_telefonica
        );
        payload.operadora = normalizeNullable(
          extractSelectValue(machineForm.value.operadora)
        );
      } else {
        payload.proprietario_chip = null;
        payload.linha_telefonica = null;
        payload.operadora = null;
      }

      return payload;
    };

    const submitMachine = async () => {
      if (!validateMachineForm()) {
        return;
      }

      saving.value = true;

      try {
        if (machineDialog.value.mode === "create") {
          await store.dispatch("adminMultpex/setMachine", createPayload());
          notifySuccess("Máquina criada com sucesso");
        } else {
          await store.dispatch("adminMultpex/updateMachine", {
            id: machineForm.value.id,
            payload: updatePayload(),
          });
          notifySuccess("Máquina atualizada com sucesso");
        }

        machineDialog.value.open = false;
        await getMachine(
          paginationAtivas.value.page,
          paginationAtivas.value.rowsPerPage
        );
        if (canViewTrashed.value) {
          await getMachineTrashed(
            paginationExcluidas.value.page,
            paginationExcluidas.value.rowsPerPage
          );
        }
      } catch (error) {
        notifyError(
          machineDialog.value.mode === "create"
            ? "Erro ao criar máquina"
            : "Erro ao atualizar máquina"
        );
      } finally {
        saving.value = false;
      }
    };

    const confirmDelete = (row) => {
      $q.dialog({
        title: "Excluir Máquina",
        message: "Tem certeza que deseja excluir esta máquina?",
        cancel: true,
        persistent: true,
      }).onOk(async () => {
        try {
          await store.dispatch("adminMultpex/deleteMachine", { id: row.id });
          notifySuccess("Máquina excluída com sucesso");
          await getMachine(
            paginationAtivas.value.page,
            paginationAtivas.value.rowsPerPage
          );
          if (canViewTrashed.value) {
            await getMachineTrashed(
              paginationExcluidas.value.page,
              paginationExcluidas.value.rowsPerPage
            );
          }
        } catch (error) {
          notifyError("Erro ao excluir máquina");
        }
      });
    };

    const confirmRestore = (row) => {
      $q.dialog({
        title: "Restaurar Máquina",
        message: "Tem certeza que deseja restaurar esta máquina?",
        cancel: true,
        persistent: true,
      }).onOk(async () => {
        try {
          await store.dispatch("adminMultpex/restoreMachine", { id: row.id });
          notifySuccess("Máquina restaurada com sucesso");
          await getMachine(
            paginationAtivas.value.page,
            paginationAtivas.value.rowsPerPage
          );
          await getMachineTrashed(
            paginationExcluidas.value.page,
            paginationExcluidas.value.rowsPerPage
          );
        } catch (error) {
          notifyError("Erro ao restaurar máquina");
        }
      });
    };

    const normalizeUsers = (response) => {
      const possibleArrays = [
        response?.data?.results?.data,
        response?.data?.data,
        response?.data?.results,
      ];

      const source = possibleArrays.find((item) => Array.isArray(item)) || [];

      return source
        .map((user) => {
          const label =
            user?.name ||
            user?.nome ||
            user?.sap ||
            user?.matricula ||
            (user?.id ? `Usuário ${user.id}` : null);

          if (!label || !user?.id) {
            return null;
          }

          return {
            label,
            value: user.id,
          };
        })
        .filter(Boolean);
    };

    const loadUsers = async () => {
      try {
        const response = await store.dispatch("adminMultpex/getUsers", {
          page: 1,
          per_page: 100,
          search: "",
        });

        const normalized = normalizeUsers(response);
        allUserOptions.value = normalized;
        userOptions.value = normalized;
      } catch (error) {
        allUserOptions.value = [];
        userOptions.value = [];
      }
    };

    const filterUserOptions = (val, update) => {
      update(() => {
        const needle = val.toLowerCase();
        userOptions.value = allUserOptions.value.filter((option) =>
          option.label.toLowerCase().includes(needle)
        );
      });
    };

    watch(
      () => paginationAtivas.value.page,
      () => {
        if (tab.value === "ativas") {
          getMachine(
            paginationAtivas.value.page,
            paginationAtivas.value.rowsPerPage
          );
        }
      }
    );

    watch(
      () => paginationExcluidas.value.page,
      () => {
        if (tab.value === "excluidas") {
          getMachineTrashed(
            paginationExcluidas.value.page,
            paginationExcluidas.value.rowsPerPage
          );
        }
      }
    );

    watch(
      () => tab.value,
      async (newTab) => {
        if (newTab === "excluidas" && canViewTrashed.value) {
          await getMachineTrashed(
            paginationExcluidas.value.page,
            paginationExcluidas.value.rowsPerPage
          );
        }
      }
    );

    watch(
      () => showMachineDimensions.value,
      (showDimensions) => {
        if (!showDimensions) {
          machineForm.value.trays = "";
          machineForm.value.columns = "";
        }
      }
    );

    watch(
      () => extractSelectValue(machineForm.value.tipo_conexao),
      (newTipoConexao) => {
        if (newTipoConexao !== "chip") {
          machineForm.value.proprietario_chip = null;
          machineForm.value.linha_telefonica = "";
          machineForm.value.operadora = null;
        }
      }
    );

    watch(
      () => store.state.org.unidadeData,
      () => {
        unidade.value = store.state.org.unidadeData;
      }
    );

    watch(
      () => vendorId.value,
      async (newVendorId, oldVendorId) => {
        if (!newVendorId) {
          return;
        }

        // Avoid duplicate load on first mount when the same vendor is already set.
        if (newVendorId === oldVendorId) {
          return;
        }

        paginationAtivas.value.page = 1;
        paginationExcluidas.value.page = 1;
        await getMachine(1, paginationAtivas.value.rowsPerPage);
        await getMachineTrashed(1, paginationExcluidas.value.rowsPerPage);
      },
      { immediate: true }
    );

    onMounted(async () => {
      getUnidade();
      await loadUsers();
    });

    return {
      tab,
      unidade,
      filter,
      filteredRowsCurrent,
      columnsCurrent,
      loadingCurrent,
      paginationCurrent,
      paginationAtivas,
      paginationExcluidas,
      pagesNumber,
      machineDialog,
      machineForm,
      detailsDialog,
      details,
      saving,
      dialogTitle,
      userOptions,
      canCreate,
      canUpdate,
      canDelete,
      canRestore,
      canViewTrashed,
      tipoOptions,
      tipoProdutoOptions,
      tipoEquipamentoOptions,
      machineTypeSelect,
      machineEquipmentTypeSelect,
      machineProductTypeSelect,
      loadingTipoOptions,
      loadingTipoProdutoOptions,
      loadingTipoEquipamentoOptions,
      tipoConexaoOptions,
      proprietarioChipOptions,
      operadoraOptions,
      showMachineDimensions,
      requiresMachineDimensions,
      traysLabel,
      columnsLabel,
      columnsMax,
      isChipConnection,
      clearFilter,
      loadMachineTypeOptions,
      loadMachineEquipmentTypeOptions,
      loadMachineProductTypeOptions,
      openMachineTypeSelect,
      openMachineEquipmentTypeSelect,
      openMachineProductTypeSelect,
      refreshCurrentTab,
      openCreateDialog,
      openEditDialog,
      openDetailsDialog,
      submitMachine,
      confirmDelete,
      confirmRestore,
      formatDateBR,
      formatPhoneNumber,
      filterUserOptions,
    };
  },
};
</script>

<style scoped>
.margem-botoes {
  margin-bottom: 2px;
}

.machine-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.machine-dialog-card {
  width: 760px;
  max-width: 96vw;
  border-radius: 8px;
}

.machine-section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #2b2b2b;
  font-size: 14px;
  font-weight: 600;
}

.machine-section-title::before,
.machine-section-title::after {
  content: "";
  flex: 1;
  border-top: 1px solid #d9deea;
}

.machine-label {
  font-size: 14px;
  font-weight: 600;
  color: #5f5f5f;
  margin-bottom: 6px;
}

.machine-field :deep(.q-field__control) {
  border-radius: 8px;
}

.pointer {
  cursor: pointer;
}

.detail-line {
  display: flex;
  align-items: center;
  gap: 6px;
}

.detail-icon {
  color: #9aa1b2;
}
</style>
